/**
 * แจ้งเตือนลืมสแกนหน้าออกงานถึงเกณฑ์ — popup ของพนักงาน (หน้าหลัก) และของ HR (AdminLayout)
 * ระบบไม่ออกใบเตือนเอง แค่บอกให้รู้ HR เป็นคนตัดสิน. อ่านข้อมูลไม่ได้ = เงียบ ไม่มี toast.
 */
import { useState } from 'react';
import {
  useForgetScanWarnings,
  useMyDeniedCapabilities,
  useMyForgetScanStatus,
  useResolveEscalation,
  type ForgetScanStatus,
  type ForgetScanWarningRow,
} from '../hooks';
import { useAuthStore } from '../lib/store';
import { useToast } from './ui/toast';
import { Modal } from './ui/Modal';

const EMPLOYEE_KEY = 'payroll-edge.forget-scan.employee-dismissed';
const HR_KEY = 'payroll-edge.forget-scan.hr-dismissed';

// AdminLayout/Home mount ใหม่ทุกครั้งที่เปลี่ยนหน้า — ถ้า storage ใช้ไม่ได้ (private mode ฯลฯ)
// ต้องจำในหน่วยความจำไว้ด้วย ไม่งั้น popup เด้งซ้ำทุกหน้า
const memoryDismissed = new Map<string, string>();

function readDismissed(getStorage: () => Storage, key: string): string | null {
  const inMemory = memoryDismissed.get(key);
  if (inMemory !== undefined) return inMemory;
  try {
    return getStorage().getItem(key);
  } catch {
    return null;
  }
}

function writeDismissed(getStorage: () => Storage, key: string, value: string): void {
  memoryDismissed.set(key, value);
  try {
    getStorage().setItem(key, value);
  } catch {
    /* ignore quota / private-mode errors */
  }
}

/** ปิดไปแล้วที่จำนวนครั้งเท่าไร — แสดงอีกเมื่อจำนวนเพิ่มจากตอนที่กดรับทราบ */
export function shouldShowEmployeeWarning(
  status: Pick<ForgetScanStatus, 'warning' | 'cumulative_count'>,
  dismissedCount: string | null,
): boolean {
  if (!status.warning) return false;
  if (dismissedCount === null) return true;
  const dismissed = Number(dismissedCount);
  return !Number.isFinite(dismissed) || status.cumulative_count > dismissed;
}

/** ชุดคน + จำนวนครั้งของ popup นี้ — เรียงก่อน ลำดับจาก API เปลี่ยนต้องไม่ทำให้เด้งซ้ำ */
export function forgetWarningSignature(rows: Array<Pick<ForgetScanWarningRow, 'emp_code' | 'cumulative_count'>>): string {
  return rows.map((r) => `${r.emp_code}:${r.cumulative_count}`).sort().join('|');
}

export function ForgetScanEmployeePopup() {
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? null);
  const empCode = useAuthStore((s) => s.profile?.emp_code ?? null);
  const { data } = useMyForgetScanStatus(!!empCode);
  const [, setDismissedTick] = useState(0);

  if (!empCode || !data) return null;
  // ผูกกับบริษัท + รหัสพนักงาน: เครื่องเดียวอาจใช้หลายบัญชี/หลายบริษัท
  const key = `${EMPLOYEE_KEY}.${tenantId ?? ''}.${empCode}`;
  const getStorage = () => window.localStorage;
  if (!shouldShowEmployeeWarning(data, readDismissed(getStorage, key))) return null;

  const dismiss = () => {
    writeDismissed(getStorage, key, String(data.cumulative_count));
    setDismissedTick((n) => n + 1);
  };

  return (
    <Modal open onClose={dismiss} title="แจ้งเตือนการลืมสแกนหน้า">
      <div className="space-y-3">
        <p className="font-medium text-gray-900">คุณลืมสแกนหน้าออกงานสะสม {data.cumulative_count} ครั้ง</p>
        <p className="text-sm text-gray-700">รอบนี้ลืมสแกนหน้าออกงาน {data.cycle_count} ครั้ง</p>
        <p className="text-sm text-amber-900 bg-amber-50 rounded p-2">
          ระบบจะแจ้งให้ HR ทราบ และ HR อาจพิจารณาออกใบเตือน กรุณาสแกนหน้าออกงานทุกครั้งเมื่อเลิกงาน
        </p>
        <button type="button" onClick={dismiss} className="btn-primary w-full">รับทราบ</button>
      </div>
    </Modal>
  );
}

export function ForgetScanHrPopup() {
  const profile = useAuthStore((s) => s.profile);
  const role = profile?.role;
  const isHr = role === 'HR';
  const denied = useMyDeniedCapabilities(isHr);
  // endpoint ใช้ requireCapability('daily_ops') ซึ่งไม่ปล่อย MANAGER/SUPERVISOR
  // HR ต้องรอผลสิทธิ์ก่อน: ระหว่างโหลดหรือโหลดพลาด ถือว่ายังไม่มีสิทธิ์ ไม่ยิงไปโดน 403
  const allowed = role === 'OWNER' || (isHr && !!denied.data && !denied.data.includes('daily_ops'));
  const { data } = useForgetScanWarnings(allowed);
  const [, setDismissedTick] = useState(0);

  // นับสะสมแล้วคนถึงเกณฑ์ค้างในรายการตลอด — เด้งเฉพาะคนที่การ์ดยังเปิด ไม่งั้น HR ปิดแล้วเจอซ้ำทุกครั้งที่เข้า
  const openRows = (data?.rows ?? []).filter((r) => r.escalation_id);
  if (!allowed || !profile || !data || openRows.length === 0) return null;
  const key = `${HR_KEY}.${profile.tenant_id ?? ''}.${profile.user_id}`;
  const signature = forgetWarningSignature(openRows);
  const getStorage = () => window.sessionStorage;
  if (readDismissed(getStorage, key) === signature) return null;

  const dismiss = () => {
    writeDismissed(getStorage, key, signature);
    setDismissedTick((n) => n + 1);
  };

  return (
    <Modal
      open
      onClose={dismiss}
      title={`พนักงานลืมสแกนออกถึงเกณฑ์ ${data.warning_at} ครั้ง — อาจมีใบเตือน`}
      size="max-w-lg"
    >
      <div className="space-y-3">
        <p className="text-sm text-amber-900 bg-amber-50 rounded p-2">
          ระบบไม่ออกใบเตือนให้เอง HR เป็นผู้พิจารณาและตัดสินใจว่าจะออกหรือไม่ ·
          เกณฑ์นี้{data.cumulative ? 'นับสะสมข้ามรอบ' : 'นับเฉพาะในรอบ'}
        </p>
        <ul className="divide-y divide-gray-100 max-h-[50vh] overflow-y-auto">
          {openRows.map((row) => <ForgetWarningItem key={row.emp_code} row={row} />)}
        </ul>
        <p className="text-xs text-gray-500">
          &ldquo;ปิดรายการนี้&rdquo; ปิดการ์ดในหน้าสรุปเวลาเข้างานเมื่อพิจารณาแล้ว ไม่เปลี่ยนจำนวนครั้งที่นับไว้
        </p>
        <button type="button" onClick={dismiss} className="btn-primary w-full">รับทราบ</button>
      </div>
    </Modal>
  );
}

/** แยกเป็น component เพื่อให้สถานะกำลังปิดเป็นของแถวนั้น ไม่ล็อกทั้งรายการ */
function ForgetWarningItem({ row }: { row: ForgetScanWarningRow }) {
  const resolve = useResolveEscalation();
  const toast = useToast();
  // แถวยังอยู่หลังปิดการ์ด เพราะรายการนี้คิดจากจำนวนครั้ง ไม่ได้คิดจากการ์ด
  const [closed, setClosed] = useState(false);

  async function closeCard(escId: string) {
    try {
      await resolve.mutateAsync({ esc_id: escId, resolution: 'resolved' });
      setClosed(true);
      toast.success('ปิดรายการแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ปิดรายการไม่สำเร็จ');
    }
  }

  return (
    <li className="py-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-gray-900 truncate">{row.name || row.emp_code}</div>
        <div className="text-[11px] text-gray-400 truncate">
          {row.department || '—'} · <span className="font-mono">{row.emp_code}</span>
        </div>
        <div className="text-xs text-gray-700">สะสม {row.cumulative_count} ครั้ง · รอบนี้ {row.cycle_count} ครั้ง</div>
      </div>
      {closed ? (
        <span className="text-xs text-gray-500 shrink-0">ปิดรายการแล้ว</span>
      ) : row.escalation_id ? (
        <button
          type="button"
          onClick={() => void closeCard(row.escalation_id!)}
          disabled={resolve.isPending}
          className="btn-secondary btn-slim shrink-0"
        >
          {resolve.isPending ? 'กำลังปิด…' : 'ปิดรายการนี้'}
        </button>
      ) : null}
    </li>
  );
}
